// DatePickerComp.tsx
import React from 'react';
import DatePicker from 'react-datepicker';
import { ReactComponent as CalendarIcon } from '../../../assets/icons/calendar.svg';
import { ReactComponent as PrevIcon } from '../../../assets/icons/prevbutton.svg';
import { ReactComponent as NextIcon } from '../../../assets/icons/nextbutton.svg';
import { months } from '../../../utils/constants';
import { subDays } from 'date-fns';
import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';
import 'react-datepicker/dist/react-datepicker.css';
import './DatePicker.scss';

type Props = {
  onDateChange: (date: Date | null) => void;
  selectedDate: Date | null;
  title?: string;
  placeholder?: string;
  showTimeInput?: boolean;
  showYearDropdown?: boolean;
  showMonthDropdown?: boolean;
  subMaxDateDays?: number;
  showUKTime?: boolean;
  maxDate?: Date | null;
  minDate?: Date | null;
  disabled?: boolean;
  timeIntervals?: number;
  yearRangeFuture?: number;
  yearRangePast?: number;
  monthOnly?: boolean;
};

const CustomInput = React.forwardRef<
  HTMLDivElement,
  { value?: string; onClick?: () => void; placeholder?: string; showUKTime?: boolean; selectedDate?: Date | null }
>(({ value, onClick, placeholder, showUKTime, selectedDate }, ref) => {
  const getUKTime = () => {
    if (!selectedDate) return null;
    return formatInTimeZone(selectedDate, 'Europe/London', 'dd/MM/yyyy hh:mm a');
  };

  const getDisplayValue = () => {
    if (!value) return placeholder || '';
    if (showUKTime && selectedDate) {
      const ukTime = getUKTime();
      return `${value} (UK: ${ukTime})`;
    }
    return value;
  };

  return (
    <div className='custom-input' onClick={onClick} ref={ref}>
      <input type='text' value={getDisplayValue()} placeholder={placeholder} readOnly />
      <CalendarIcon className='calendar-icon' />
    </div>
  );
});

const DatePickerComp: React.FC<Props> = ({
  onDateChange,
  title,
  placeholder,
  selectedDate,
  showTimeInput,
  showYearDropdown = true,
  showMonthDropdown = true,
  subMaxDateDays = 0,
  showUKTime = false,
  maxDate,
  minDate,
  disabled,
  timeIntervals = 1,
  yearRangeFuture = 50,
  yearRangePast = 120,
  monthOnly = false
}) => {
  // Convert UTC date to local time for display in the picker
  const displayDate = selectedDate ? toZonedTime(selectedDate, Intl.DateTimeFormat().resolvedOptions().timeZone) : null;

  const handleDateChange = (date: Date | null) => {
    if (date) {
      // Convert the local date back to UTC before passing to parent
      const utcDate = fromZonedTime(date, Intl.DateTimeFormat().resolvedOptions().timeZone);
      onDateChange(utcDate);
    } else {
      onDateChange(null);
    }
  };

  return (
    <div className='date-picker-container'>
      {title && <div className='title'>{title}</div>}
      <DatePicker
        showIcon
        shouldCloseOnSelect
        disabledKeyboardNavigation
        showYearDropdown={showYearDropdown}
        showMonthYearPicker={monthOnly}
        selected={displayDate}
        disabled={disabled}
        // When the date changes, convert the picker date back to a UTC Date before calling onDateChange.
        onChange={handleDateChange}
        placeholderText={placeholder}
        customInput={<CustomInput placeholder={placeholder} showUKTime={showUKTime} selectedDate={selectedDate} />}
        renderCustomHeader={({
          date,
          decreaseMonth,
          increaseMonth,
          changeYear,
          changeMonth
        }) => (
          <div className='date-picker-header'>
            <div onClick={decreaseMonth}>
              <PrevIcon />
            </div>
            <div className='header-date'>
              {showMonthDropdown ? (
                <select
                  value={date?.getMonth()}
                  onChange={({ target: { value } }) => changeMonth(Number(value))}
                >
                  {months.map((month, idx) => (
                    <option key={month} value={idx}>
                      {month}
                    </option>
                  ))}
                </select>
              ) : (
                <>
                  {months[date?.getMonth()]}{' '}
                </>
              )}
              {showYearDropdown ? (
                <select
                  value={date?.getFullYear()}
                  onChange={({ target: { value } }) =>
                    changeYear(Number(value))
                  }
                >
                  {Array.from(
                    { length: yearRangeFuture + yearRangePast + 1 },
                    (_, i) => {
                      const currentYear = new Date().getFullYear();
                      const year = currentYear + yearRangeFuture - i;
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    }
                  )}
                </select>
              ) : (
                date?.getFullYear()
              )}
            </div>
            <div onClick={increaseMonth}>
              <NextIcon />
            </div>
          </div>
        )}
        formatWeekDay={(nameOfDay) => nameOfDay.substr(0, 3)}
        useWeekdaysShort={true}
        calendarStartDay={1}
        showTimeSelect={showTimeInput}
        showTimeInput={showTimeInput}
        {...(showTimeInput ? { timeIntervals, timeInputLabel: 'Time' } : {})}
        dateFormat={monthOnly ? 'MM/yyyy' : showTimeInput ? 'dd/MM/yyyy hh:mm a' : 'dd/MM/yyyy'}
        maxDate={
          maxDate
            ? toZonedTime(maxDate, Intl.DateTimeFormat().resolvedOptions().timeZone)
            : subMaxDateDays
              ? subDays(new Date(), subMaxDateDays)
              : undefined
        }
        minDate={
          minDate
            ? toZonedTime(minDate, Intl.DateTimeFormat().resolvedOptions().timeZone)
            : undefined
        }
      />
    </div>
  );
};

export default DatePickerComp;
